/**
 * Receipts Service
 * Handles receipt-related API calls
 */

import { apiClient } from "../client";
import type {
  Receipt,
  ReceiptListResponse,
  CreateReceiptRequest,
  CreateReceiptResponse,
  FilterParams,
} from "../types";

export const receiptsService = {
  /**
   * Get list of receipts
   * Endpoint: DirectorModule/receipts
   */
  async getReceipts(params?: FilterParams): Promise<ReceiptListResponse> {
    const response = await apiClient.get<ReceiptListResponse>("DirectorModule/receipts", params);
    return response.data;
  },

  /**
   * Get single receipt by ID
   */
  async getReceiptById(receiptId: string): Promise<Receipt> {
    const response = await apiClient.get<Receipt>(`/receipts/${receiptId}`);
    return response.data;
  },

  /**
   * Create new receipt
   */
  async createReceipt(data: CreateReceiptRequest): Promise<CreateReceiptResponse> {
    const response = await apiClient.post<CreateReceiptResponse>("/receipts", data);
    return response.data;
  },

  /**
   * Update receipt
   */
  async updateReceipt(receiptId: string, data: Partial<Receipt>): Promise<Receipt> {
    const response = await apiClient.put<Receipt>(`/receipts/${receiptId}`, data);
    return response.data;
  },

  /**
   * Delete receipt
   */
  async deleteReceipt(receiptId: string): Promise<void> {
    await apiClient.delete(`/receipts/${receiptId}`);
  },

  /**
   * Get receipt summary
   */
  async getReceiptSummary(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<{
    totalAmount: number;
    count: number;
    byPaymentMode: Record<string, number>;
  }> {
    const response = await apiClient.get<{
      totalAmount: number;
      count: number;
      byPaymentMode: Record<string, number>;
    }>("/receipts/summary", params);
    return response.data;
  },
};

